import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/client';
import { useAuth } from '../AdminRoutes';
import { useApi, formatNumber } from '../hooks/useApi';
import { Card, EmptyState, ErrorState, TableSkeleton, Drawer } from '../components/ui';

/**
 * Edits the six programmes shown on /training-internship.
 *
 * The public page reads these from the API, so a change here is live on the
 * site immediately — no redeploy. Turning a track off removes it from the page
 * and from the application form's dropdown, while keeping every application
 * already submitted against it.
 */
export default function Tracks() {
  const { canWrite } = useAuth();
  const tracks = useApi('/api/admin/tracks');
  const [editing, setEditing] = useState(null);

  return (
    <div className="spa-stack">
      <Card
        title="Internship tracks"
        note="What appears on the Training & Internship page. Changes go live immediately."
        padded={false}
      >
        {tracks.error ? (
          <ErrorState message={tracks.error} onRetry={tracks.reload} />
        ) : tracks.isLoading && !tracks.data ? (
          <TableSkeleton cols={6} />
        ) : !tracks.data?.length ? (
          <EmptyState icon="fa-graduation-cap" title="No tracks configured"
            text="Run the seed script to load the six default programmes." />
        ) : (
          <div className="spa-tablewrap">
            <table className="spa-table">
              <thead>
                <tr>
                  <th>Track</th><th className="num">Seats</th><th>Duration</th>
                  <th>Mode</th><th className="num">Applications</th><th>On the site</th><th />
                </tr>
              </thead>
              <tbody>
                {tracks.data.map((t) => (
                  <tr key={t.slug}>
                    <td>
                      <div className="spa-table__primary">{t.title}</div>
                      <div className="spa-table__sub">{t.tagline}</div>
                    </td>
                    <td className="num">{t.seats}</td>
                    <td>{t.duration}</td>
                    <td>{t.mode}</td>
                    <td className="num">{formatNumber(t.application_count)}</td>
                    <td>
                      <span className={`spa-pill spa-pill--${t.is_active ? 'green' : 'neutral'}`}>
                        {t.is_active ? 'Visible' : 'Hidden'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button type="button" className="spa-btn spa-btn--sm"
                        onClick={() => setEditing(t)} disabled={!canWrite}>
                        {canWrite ? 'Edit' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <TrackEditor
        track={editing}
        canWrite={canWrite}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); tracks.reload(); }}
      />
    </div>
  );
}

function TrackEditor({ track, canWrite, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [isBusy, setIsBusy] = useState(false);

  // Reload the draft whenever a different track is opened.
  useEffect(() => {
    setForm(track ? { ...track, skills: (track.skills || []).join(', ') } : null);
  }, [track]);

  if (!track || !form) return null;

  const set = (name) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [name]: v }));
  };

  const save = async () => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      const { data } = await api.patch(`/api/admin/tracks/${track.slug}`, {
        title: form.title,
        tagline: form.tagline,
        description: form.description,
        duration: form.duration,
        mode: form.mode,
        stipend: form.stipend,
        seats: Number(form.seats) || 0,
        skills: String(form.skills).split(',').map((s) => s.trim()).filter(Boolean),
        image_url: form.image_url,
        is_active: form.is_active,
        sort_order: Number(form.sort_order) || 0,
      });
      toast.success(data.message);
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsBusy(false);
    }
  };

  const Row = ({ label, name, type = 'text', hint, ...rest }) => (
    <div className="spa-login__field">
      <label className="spa-login__label" htmlFor={`t-${name}`}>{label}</label>
      {type === 'textarea' ? (
        <textarea id={`t-${name}`} className="spa-textarea" value={form[name] ?? ''}
          onChange={set(name)} disabled={!canWrite} {...rest} />
      ) : (
        <input id={`t-${name}`} className="spa-input" type={type} value={form[name] ?? ''}
          onChange={set(name)} disabled={!canWrite} {...rest} />
      )}
      {hint && <p className="spa-card__note" style={{ marginTop: 5 }}>{hint}</p>}
    </div>
  );

  return (
    <Drawer
      open
      onClose={onClose}
      title={track.title}
      subtitle={`${formatNumber(track.application_count)} applications received`}
      footer={canWrite && (
        <>
          <button type="button" className="spa-btn spa-btn--primary" onClick={save} disabled={isBusy}>
            {isBusy ? 'Saving…' : 'Save changes'}
          </button>
          <button type="button" className="spa-btn" onClick={onClose}>Cancel</button>
        </>
      )}
    >
      <div className="spa-section-label">On the site</div>
      <div className="spa-check" style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <input type="checkbox" id="t-active" checked={Boolean(form.is_active)}
          onChange={set('is_active')} disabled={!canWrite}
          style={{ width: 17, height: 17, marginTop: 3, accentColor: '#d92d20' }} />
        <label htmlFor="t-active" style={{ fontSize: '0.88rem', lineHeight: 1.6 }}>
          Show this track on the Training &amp; Internship page and in the application
          form. Hiding it keeps every application already submitted against it.
        </label>
      </div>

      <div className="spa-section-label">Copy</div>
      <Row label="Title" name="title" />
      <Row label="Tagline" name="tagline" hint="One line, shown in red under the title." />
      <Row label="Description" name="description" type="textarea" rows={5} />

      <div className="spa-section-label">Details</div>
      <Row label="Duration" name="duration" hint="For example: 8 weeks" />
      <Row label="Mode" name="mode" hint="Onsite, Hybrid or Remote" />
      <Row label="Stipend" name="stipend" />
      <Row label="Seats" name="seats" type="number" min="0"
        hint="Shown on the card and totalled in the page's hero badge." />
      <Row label="Skills" name="skills"
        hint="Comma separated. These become the selectable chips on the application form." />

      <div className="spa-section-label">Presentation</div>
      <Row label="Image path" name="image_url"
        hint="A path under /public, for example /assets/images/about/about-4.jpg" />
      <Row label="Sort order" name="sort_order" type="number"
        hint="Lower numbers appear first." />

      {form.image_url && (
        <img
          src={form.image_url}
          alt=""
          style={{
            width: '100%', maxHeight: 150, objectFit: 'cover',
            borderRadius: 4, border: '1px solid var(--spa-rule)', marginTop: 8,
          }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      )}
    </Drawer>
  );
}
